/* ── Scroll Reveal ── */

class ScrollReveal extends HTMLElement {
  connectedCallback() {
    if (document.body.classList.contains('no-animations')) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const elements = this.querySelectorAll('.reveal, .reveal--scale, .reveal--fade');
    if (!elements.length) return;

    const stagger = this.hasAttribute('data-stagger');
    if (stagger) {
      elements.forEach((el, i) => el.style.setProperty('--i', i));
    }

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          this.observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });

    elements.forEach((el) => this.observer.observe(el));
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }
}

if (!customElements.get('scroll-reveal')) {
  customElements.define('scroll-reveal', ScrollReveal);
}

/* ── Smooth Details (Accordion Animation) ── */

class SmoothDetails extends HTMLElement {
  connectedCallback() {
    this.details = this.querySelectorAll('details');
    if (!this.details.length) return;

    this.details.forEach((detail) => {
      const summary = detail.querySelector('summary');
      if (!summary) return;

      summary.addEventListener('click', (e) => {
        e.preventDefault();
        if (detail.open) {
          this.close(detail);
        } else {
          this.open(detail);
        }
      });
    });
  }

  open(detail) {
    detail.open = true;
    const content = detail.querySelector('.faq-item__answer');
    if (!content) return;

    const height = content.scrollHeight;
    content.style.height = '0px';
    content.style.overflow = 'hidden';

    requestAnimationFrame(() => {
      content.animate(
        [{ height: '0px', opacity: 0 }, { height: height + 'px', opacity: 1 }],
        { duration: 320, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' }
      ).onfinish = () => {
        content.style.height = '';
        content.style.overflow = '';
      };
    });
  }

  close(detail) {
    const content = detail.querySelector('.faq-item__answer');
    if (!content) return;

    const height = content.scrollHeight;
    content.animate(
      [{ height: height + 'px', opacity: 1 }, { height: '0px', opacity: 0 }],
      { duration: 260, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' }
    ).onfinish = () => {
      detail.open = false;
      content.style.height = '';
      content.style.overflow = '';
    };
  }
}

if (!customElements.get('smooth-details')) {
  customElements.define('smooth-details', SmoothDetails);
}

/* ── Header Drawer ── */

class HeaderDrawer extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-drawer-toggle]');
    this.drawer = this.querySelector('[data-mobile-drawer]');
    this.backdrop = this.querySelector('[data-drawer-backdrop]');

    if (!this.button || !this.drawer) return;

    this.button.addEventListener('click', () => this.toggle());
    this.backdrop?.addEventListener('click', () => this.close());

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.drawer.classList.contains('is-open')) {
        this.close();
      }
    });
  }

  toggle() {
    if (this.drawer.classList.contains('is-open')) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    this.drawer.classList.add('is-open');
    this.backdrop?.classList.add('is-open');
    this.button.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
  }

  close() {
    this.drawer.classList.remove('is-open');
    this.backdrop?.classList.remove('is-open');
    this.button.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

class ProductVideoRail extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this.track = this.querySelector('[data-video-rail-track]');
    this.prev = this.querySelector('[data-video-rail-prev]');
    this.next = this.querySelector('[data-video-rail-next]');
    this.dots = Array.from(this.querySelectorAll('[data-video-rail-dot]'));

    if (!this.track) return;

    this.isAnimating = false;
    this.activeIndex = 0;
    this.position = 0;
    this.dragOffset = 0;
    this.dragState = null;
    this.suppressClick = false;
    this.autoplayTimer = null;
    this.autoplayPaused = false;
    this.autoplayEnabled = this.dataset.videoRailAutoplay === 'true';
    this.autoplayInterval = Number(this.dataset.videoRailInterval || 5000);
    this.pauseOnHover = this.dataset.videoRailPauseOnHover !== 'false';
    this.handleResize = () => this.jumpToIndex(this.activeIndex);
    this.handleVisibilityChange = () => {
      if (document.hidden) {
        this.clearAutoplay();
      } else {
        this.queueAutoplay();
      }
    };
    this.handlePointerDown = (event) => this.startPointerDrag(event);
    this.handlePointerMove = (event) => this.movePointerDrag(event);
    this.handlePointerUp = (event) => this.endPointerDrag(event);
    this.handlePointerCancel = (event) => this.cancelPointerDrag(event);
    this.setupRail();
    this.prev?.addEventListener('click', () => this.handleManualScroll(-1));
    this.next?.addEventListener('click', () => this.handleManualScroll(1));
    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => this.handleManualIndex(Number(dot.dataset.videoRailDot || 0)));
    });
    this.track.addEventListener('click', (event) => this.handleCardClick(event));
    this.track.addEventListener('pointerdown', this.handlePointerDown);
    this.track.addEventListener('pointermove', this.handlePointerMove);
    this.track.addEventListener('pointerup', this.handlePointerUp);
    this.track.addEventListener('pointercancel', this.handlePointerCancel);
    this.track.addEventListener('lostpointercapture', this.handlePointerCancel);
    window.addEventListener('resize', this.handleResize);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);

    if (this.pauseOnHover) {
      this.addEventListener('mouseenter', () => this.pauseAutoplay());
      this.addEventListener('mouseleave', () => this.resumeAutoplay());
      this.addEventListener('focusin', () => this.pauseAutoplay());
      this.addEventListener('focusout', () => {
        window.setTimeout(() => {
          if (!this.contains(document.activeElement)) this.resumeAutoplay();
        }, 0);
      });
    }

    this.updatePagination();
    this.queueAutoplay();
  }

  disconnectedCallback() {
    this.clearAutoplay();
    window.clearTimeout(this.animationTimer);

    if (this.handleResize) window.removeEventListener('resize', this.handleResize);
    if (this.handleVisibilityChange) document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    if (this.track) {
      this.track.removeEventListener('pointerdown', this.handlePointerDown);
      this.track.removeEventListener('pointermove', this.handlePointerMove);
      this.track.removeEventListener('pointerup', this.handlePointerUp);
      this.track.removeEventListener('pointercancel', this.handlePointerCancel);
      this.track.removeEventListener('lostpointercapture', this.handlePointerCancel);
    }
  }

  scroll(direction) {
    if (!this.originalCards?.length) return;

    this.position += direction;
    this.activeIndex = this.wrapIndex(Math.round(this.position));
    this.updatePagination();
    this.renderCards(true);
  }

  handleManualScroll(direction) {
    this.scroll(direction);
    this.restartAutoplay();
  }

  setupRail() {
    this.originalCards = Array.from(this.track.querySelectorAll('.product-video-card'));

    this.originalCards.forEach((card, index) => {
      card.dataset.caseIndex = String(index);
    });

    this.jumpToIndex(0);
    window.requestAnimationFrame(() => this.jumpToIndex(this.activeIndex));
  }

  handleCardClick(event) {
    if (this.suppressClick) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    const card = event.target.closest('[data-video-rail-card]');
    if (!card || !this.track.contains(card)) return;

    const index = Number(card.dataset.caseIndex);
    if (Number.isNaN(index)) return;

    this.scrollToIndex(index);
    this.restartAutoplay();
  }

  handleManualIndex(index) {
    this.scrollToIndex(index);
    this.restartAutoplay();
  }

  scrollToIndex(index) {
    if (!this.originalCards?.length) return;

    const count = this.originalCards.length;
    const targetIndex = this.wrapIndex(index);
    const currentIndex = this.wrapIndex(Math.round(this.position));
    let delta = targetIndex - currentIndex;

    if (delta > count / 2) delta -= count;
    if (delta < count / -2) delta += count;

    this.position += delta;
    this.activeIndex = targetIndex;
    this.updatePagination();
    this.renderCards(true);
  }

  startPointerDrag(event) {
    if (!this.originalCards?.length || this.originalCards.length < 2) return;
    if (event.button !== undefined && event.button !== 0) return;

    this.dragState = {
      id: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      dragging: false
    };
    this.clearAutoplay();
    this.track.setPointerCapture?.(event.pointerId);
  }

  movePointerDrag(event) {
    if (!this.dragState || event.pointerId !== this.dragState.id) return;

    const deltaX = event.clientX - this.dragState.startX;
    const deltaY = event.clientY - this.dragState.startY;
    const absX = Math.abs(deltaX);
    const absY = Math.abs(deltaY);

    if (!this.dragState.dragging && absX > 8 && absX > absY) {
      this.dragState.dragging = true;
      this.track.classList.add('is-dragging');
    }

    if (!this.dragState.dragging) return;

    event.preventDefault();
    const stride = this.getCardStride();
    this.dragOffset = Math.max(Math.min(deltaX, stride * 0.42), stride * -0.42);
    this.renderCards(false);
  }

  endPointerDrag(event) {
    if (!this.dragState || event.pointerId !== this.dragState.id) return;

    const deltaX = event.clientX - this.dragState.startX;
    const deltaY = event.clientY - this.dragState.startY;
    const stride = this.getCardStride();
    const threshold = Math.max(42, Math.min(120, stride * 0.16));
    const didSwipe = this.dragState.dragging && Math.abs(deltaX) > threshold && Math.abs(deltaX) > Math.abs(deltaY);

    this.track.classList.remove('is-dragging');
    this.dragState = null;
    this.track.releasePointerCapture?.(event.pointerId);
    this.dragOffset = 0;

    if (didSwipe) {
      this.suppressClick = true;
      this.scroll(deltaX < 0 ? 1 : -1);
      window.setTimeout(() => {
        this.suppressClick = false;
      }, 120);
    } else {
      this.renderCards(true);
      this.queueAutoplay();
    }
  }

  cancelPointerDrag(event) {
    if (!this.dragState || event.pointerId !== this.dragState.id) return;

    this.track.classList.remove('is-dragging');
    this.dragState = null;
    this.track.releasePointerCapture?.(event.pointerId);
    this.dragOffset = 0;
    this.renderCards(true);
    this.queueAutoplay();
  }

  renderCards(animate = true) {
    if (!this.originalCards?.length) return;

    window.clearTimeout(this.animationTimer);
    this.track.classList.toggle('is-instant', !animate);

    const stride = this.getCardStride();
    const visibleRadius = this.originalCards.length > 2 ? 1 : 0;

    this.originalCards.forEach((card, index) => {
      const offset = this.getCircularOffset(index);
      const distance = Math.abs(offset);
      const translateX = Math.round(offset * stride + this.dragOffset);

      card.style.transform = `translate3d(calc(-50% + ${translateX}px), 0, 0)`;
      card.style.opacity = distance <= visibleRadius ? '1' : '0';
      card.style.pointerEvents = distance <= visibleRadius ? 'auto' : 'none';
      card.style.zIndex = String(100 - distance);
      card.classList.toggle('is-active', index === this.activeIndex);
      card.setAttribute('aria-hidden', distance <= visibleRadius ? 'false' : 'true');
    });

    if (!animate) {
      this.track.offsetHeight;
      window.requestAnimationFrame(() => this.track.classList.remove('is-instant'));
      return;
    }

    this.isAnimating = true;
    this.animationTimer = window.setTimeout(() => this.finishAnimatedScroll(), 620);
  }

  getCircularOffset(index) {
    const count = this.originalCards.length;
    const normalizedPosition = ((this.position % count) + count) % count;
    let offset = index - normalizedPosition;

    if (offset > count / 2) offset -= count;
    if (offset <= count / -2) offset += count;

    return offset;
  }

  wrapIndex(index) {
    const count = this.originalCards.length;

    return ((index % count) + count) % count;
  }

  getCardStride() {
    const activeCard = this.originalCards[this.activeIndex] || this.originalCards[0];
    const styles = window.getComputedStyle(this.track);
    const gap = Number.parseFloat(styles.getPropertyValue('--video-rail-card-gap')) || 18;
    const width = activeCard?.getBoundingClientRect().width || Math.min(window.innerWidth * 0.78, 860);

    return width + gap;
  }

  jumpToIndex(index) {
    if (!this.originalCards?.length) return;

    this.activeIndex = this.wrapIndex(index);
    this.position = this.activeIndex;
    this.updatePagination();
    this.renderCards(false);
  }

  animateTo(card) {
    if (!card) return;

    const index = Number(card.dataset.caseIndex);
    if (Number.isNaN(index)) return;

    this.scrollToIndex(index);
  }

  finishAnimatedScroll() {
    if (!this.originalCards?.length) return;

    window.clearTimeout(this.animationTimer);
    this.isAnimating = false;
  }

  getNearestOriginalIndex() {
    return this.activeIndex || 0;
  }

  updatePagination() {
    this.dots?.forEach((dot, index) => {
      const active = index === this.activeIndex;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-current', active ? 'true' : 'false');
    });

  }

  shouldAutoplay() {
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    return this.autoplayEnabled
      && !reduceMotion
      && !document.hidden
      && this.originalCards?.length > 1;
  }

  clearAutoplay() {
    window.clearTimeout(this.autoplayTimer);
    this.autoplayTimer = null;
  }

  queueAutoplay() {
    this.clearAutoplay();

    if (!this.shouldAutoplay() || this.autoplayPaused) return;

    this.autoplayTimer = window.setTimeout(() => {
      this.scroll(1);
      this.queueAutoplay();
    }, Math.max(this.autoplayInterval, 1000));
  }

  restartAutoplay() {
    if (this.autoplayPaused) return;

    this.queueAutoplay();
  }

  pauseAutoplay() {
    this.autoplayPaused = true;
    this.clearAutoplay();
  }

  resumeAutoplay() {
    this.autoplayPaused = false;
    this.queueAutoplay();
  }
}

if (!customElements.get('product-video-rail')) {
  customElements.define('product-video-rail', ProductVideoRail);
}

class ProductTemplateOne extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this.mainImage = this.querySelector('[data-product-template-one-main], .product-template-one__main-image');
    this.thumbs = Array.from(this.querySelectorAll('[data-product-template-one-thumb]'));
    this.activeIndex = 0;
    this.touchStartX = 0;

    if (!this.mainImage || !this.thumbs.length) return;

    this.thumbs.forEach((thumb, index) => {
      thumb.addEventListener('click', () => this.showIndex(index));
    });

    this.mainImage.addEventListener('touchstart', (event) => {
      this.touchStartX = event.touches[0]?.clientX || 0;
    }, { passive: true });

    this.mainImage.addEventListener('touchend', (event) => {
      const touchEndX = event.changedTouches[0]?.clientX || 0;
      const delta = touchEndX - this.touchStartX;
      if (Math.abs(delta) < 42) return;
      this.showIndex(this.activeIndex + (delta < 0 ? 1 : -1));
    });
  }

  showIndex(index) {
    const nextIndex = (index + this.thumbs.length) % this.thumbs.length;
    const thumb = this.thumbs[nextIndex];
    const nextSrc = thumb?.dataset.productTemplateOneThumb;
    if (!nextSrc) return;

    this.activeIndex = nextIndex;
    this.mainImage.src = nextSrc;
    this.mainImage.removeAttribute('srcset');
    this.thumbs.forEach((item) => item.classList.toggle('is-active', item === thumb));
  }
}

if (!customElements.get('product-template-one')) {
  customElements.define('product-template-one', ProductTemplateOne);
}

/* ── Product variants, AJAX add and cart drawer ── */

class ProductVariantController {
  constructor(root) {
    this.root = root;
    this.form = root.querySelector('form[action*="/cart/add"]');
    this.variantData = this.readVariants();
    this.price = root.querySelector('[data-variant-price]');
    this.availability = root.querySelector('[data-variant-availability]');
    this.submit = root.querySelector('[data-add-to-cart-text], button[name="add"]');
    this.availableText = this.availability?.textContent?.trim() || 'Ready to ship';
    this.addText = this.submit?.textContent?.trim() || 'Add to cart';
    this.soldOutText = window.themeStrings?.soldOut || 'Sold out';
    this.moneyFormat = root.dataset.moneyFormat || window.themeMoneyFormat || '${{amount}}';

    if (!this.form || !this.variantData.length) return;

    this.form.addEventListener('change', (event) => {
      if (event.target.matches('[name="id"]')) this.updateSelectedVariant();
    });

    this.form.addEventListener('submit', (event) => this.handleSubmit(event));
    this.updateSelectedVariant();
  }

  readVariants() {
    const script = this.root.querySelector('[data-product-variants]');
    if (!script) return [];

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.warn('Unable to parse product variants:', error);
      return [];
    }
  }

  getSelectedId() {
    const checked = this.form.querySelector('[name="id"]:checked');
    const selected = checked || this.form.querySelector('[name="id"]');
    return Number(selected?.value);
  }

  getSelectedVariant() {
    const selectedId = this.getSelectedId();
    return this.variantData.find((variant) => Number(variant.id) === selectedId);
  }

  updateSelectedVariant() {
    const variant = this.getSelectedVariant();
    if (!variant) return;

    if (this.price) this.price.textContent = formatThemeMoney(variant.price, this.moneyFormat);
    if (this.availability) this.availability.textContent = variant.available ? this.availableText : this.soldOutText;

    if (this.submit) {
      this.submit.disabled = !variant.available;
      this.submit.textContent = variant.available ? this.addText : this.soldOutText;
    }
  }

  async handleSubmit(event) {
    if (!window.fetch || this.form.dataset.ajaxDisabled === 'true') return;

    event.preventDefault();
    const variant = this.getSelectedVariant();
    if (variant && !variant.available) return;

    this.setLoading(true);

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(this.form)
      });

      if (!response.ok) throw new Error('Add to cart failed');

      const item = await response.json();
      const cartResponse = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = await cartResponse.json();

      updateCartCount(cart.item_count);
      window.dispatchEvent(new CustomEvent('theme:cart-updated', { detail: { cart, item } }));
    } catch (error) {
      console.error('Add to cart failed:', error);
      this.form.submit();
    } finally {
      this.setLoading(false);
    }
  }

  setLoading(loading) {
    if (!this.submit) return;

    this.submit.classList.toggle('is-loading', loading);
    this.submit.disabled = loading || !this.getSelectedVariant()?.available;
  }
}

class CartDrawer {
  constructor(root) {
    this.root = root;
    this.panel = root.querySelector('[data-cart-drawer-panel]');
    this.body = root.querySelector('[data-cart-drawer-body]');
    this.total = root.querySelector('[data-cart-drawer-total]');
    this.closeButtons = root.querySelectorAll('[data-cart-drawer-close]');

    this.closeButtons.forEach((button) => button.addEventListener('click', () => this.close()));
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.root.classList.contains('is-open')) this.close();
    });
    window.addEventListener('theme:cart-updated', (event) => this.render(event.detail.cart));
  }

  render(cart) {
    if (!cart || !this.body) return;

    this.body.innerHTML = cart.items.map((item) => `
      <a class="cart-drawer__item" href="${item.url}">
        ${item.image ? `<img src="${item.image}" alt="${escapeHtml(item.product_title)}" width="76" height="76" loading="lazy">` : '<span></span>'}
        <span>
          <strong>${escapeHtml(item.product_title)}</strong>
          <small>${item.variant_title ? escapeHtml(item.variant_title) + ' · ' : ''}${item.quantity} × ${formatThemeMoney(item.final_price)}</small>
        </span>
      </a>
    `).join('');

    if (this.total) this.total.textContent = formatThemeMoney(cart.total_price);
    this.open();
  }

  open() {
    this.root.classList.add('is-open');
    this.root.removeAttribute('hidden');
    window.setTimeout(() => this.panel?.focus(), 0);
  }

  close() {
    this.root.classList.remove('is-open');
  }
}

function formatThemeMoney(cents, format = window.themeMoneyFormat || '${{amount}}') {
  if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents, format);

  const value = (Number(cents || 0) / 100).toFixed(2);
  return format
    .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(Number(cents || 0) / 100).toString())
    .replace(/\{\{\s*amount\s*\}\}/, value)
    .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, value.replace('.', ','));
}

function escapeHtml(value) {
  const div = document.createElement('div');
  div.textContent = value || '';
  return div.innerHTML;
}

function updateCartCount(count) {
  document.querySelectorAll('[data-cart-count]').forEach((badge) => {
    badge.textContent = count;
    badge.hidden = Number(count) === 0;
  });
}

function initQuickAddForms() {
  document.querySelectorAll('[data-product-card-form]').forEach((form) => {
    if (form.dataset.initialized === 'true') return;
    form.dataset.initialized = 'true';

    form.addEventListener('submit', async (event) => {
      if (!window.fetch) return;

      event.preventDefault();
      const button = form.querySelector('button[name="add"]');
      button?.classList.add('is-loading');
      if (button) button.disabled = true;

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form)
        });

        if (!response.ok) throw new Error('Quick add failed');

        const item = await response.json();
        const cartResponse = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        const cart = await cartResponse.json();

        updateCartCount(cart.item_count);
        window.dispatchEvent(new CustomEvent('theme:cart-updated', { detail: { cart, item } }));
      } catch (error) {
        console.error('Quick add failed:', error);
        form.submit();
      } finally {
        button?.classList.remove('is-loading');
        if (button) button.disabled = false;
      }
    });
  });
}

function randomizeProductGrid(grid, visibleCount) {
  if (!grid) return;

  const cards = Array.from(grid.children);
  const cardCount = Number(visibleCount || grid.dataset.cardCount || cards.length);

  for (let index = cards.length - 1; index > 0; index -= 1) {
    const randomIndex = Math.floor(Math.random() * (index + 1));
    [cards[index], cards[randomIndex]] = [cards[randomIndex], cards[index]];
  }

  cards.forEach((card, index) => {
    card.hidden = index >= cardCount;
    grid.appendChild(card);
  });

  grid.dataset.randomizedInitialized = 'true';
}

function initRandomizedProductGrids(root = document) {
  root.querySelectorAll('[data-randomized-product-grid]').forEach((grid) => {
    if (grid.dataset.randomizedInitialized === 'true') return;
    if (grid.closest('product-recommendations')) return;
    randomizeProductGrid(grid, grid.dataset.cardCount);
  });
}

function initCollectionFilters() {
  document.querySelectorAll('[data-collection-filter-drawer]').forEach((drawer) => {
    const openButtons = document.querySelectorAll(`[data-collection-filter-open][aria-controls="${drawer.id}"]`);
    const closeButtons = drawer.querySelectorAll('[data-collection-filter-close]');

    const open = () => {
      drawer.hidden = false;
      document.body.style.overflow = 'hidden';
    };
    const close = () => {
      drawer.hidden = true;
      document.body.style.overflow = '';
    };

    openButtons.forEach((button) => button.addEventListener('click', open));
    closeButtons.forEach((button) => button.addEventListener('click', close));
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !drawer.hidden) close();
    });
  });
}

function initProductLightbox() {
  const images = document.querySelectorAll('.main-product__media img, .product-template-one__main-image');
  if (!images.length) return;

  const lightbox = document.createElement('dialog');
  lightbox.className = 'product-lightbox';
  lightbox.innerHTML = '<button class="product-lightbox__close" type="button" aria-label="Close">×</button><button class="product-lightbox__nav product-lightbox__nav--prev" type="button" aria-label="Previous image">‹</button><img alt="" width="1200" height="1200"><button class="product-lightbox__nav product-lightbox__nav--next" type="button" aria-label="Next image">›</button>';
  document.body.appendChild(lightbox);

  const lightboxImage = lightbox.querySelector('img');
  const closeButton = lightbox.querySelector('.product-lightbox__close');
  const prevButton = lightbox.querySelector('.product-lightbox__nav--prev');
  const nextButton = lightbox.querySelector('.product-lightbox__nav--next');
  let gallery = [];
  let activeIndex = 0;
  let triggerImage = null;

  const updateImage = () => {
    const item = gallery[activeIndex];
    if (!item) return;
    lightboxImage.src = item.src;
    lightboxImage.alt = item.alt || '';
    const hasMultiple = gallery.length > 1;
    prevButton.hidden = !hasMultiple;
    nextButton.hidden = !hasMultiple;
  };

  const close = () => {
    if (lightbox.open) lightbox.close();
  };
  const step = (direction) => {
    if (gallery.length < 2) return;
    activeIndex = (activeIndex + direction + gallery.length) % gallery.length;
    updateImage();
  };

  images.forEach((image) => {
    image.classList.add('is-zoomable');
    image.setAttribute('tabindex', '0');
    image.setAttribute('role', 'button');
    const open = () => {
      triggerImage = image;
      const productShell = image.closest('[data-product-template-one]');
      const thumbs = productShell ? Array.from(productShell.querySelectorAll('[data-product-template-one-thumb]')) : [];
      gallery = thumbs.length
        ? thumbs.map((thumb) => ({ src: thumb.dataset.productTemplateOneThumb, alt: image.alt || '' })).filter((item) => item.src)
        : [{ src: image.currentSrc || image.src, alt: image.alt || '' }];
      activeIndex = Math.max(0, thumbs.findIndex((thumb) => thumb.classList.contains('is-active')));
      updateImage();
      lightbox.showModal();
      closeButton.focus();
    };
    image.addEventListener('click', open);
    image.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        open();
      }
    });
  });

  closeButton.addEventListener('click', close);
  prevButton.addEventListener('click', () => step(-1));
  nextButton.addEventListener('click', () => step(1));
  lightbox.addEventListener('click', (event) => {
    if (event.target === lightbox) close();
  });
  lightbox.addEventListener('close', () => {
    triggerImage?.focus();
    triggerImage = null;
  });
  document.addEventListener('keydown', (event) => {
    if (lightbox.open && event.key === 'ArrowLeft') step(-1);
    if (lightbox.open && event.key === 'ArrowRight') step(1);
  });
}

function hideMissingProductAnchors() {
  document.querySelectorAll('.product-local-nav__links a[href^="#"], .main-product__quick-actions a[href^="#"]').forEach((link) => {
    const target = document.querySelector(link.getAttribute('href'));
    if (!target) link.hidden = true;
  });
}

function initContactValidation() {
  document.querySelectorAll('.contact-form__fields').forEach((form) => {
    form.addEventListener('submit', (event) => {
      const invalid = Array.from(form.querySelectorAll('[required]')).find((field) => !field.checkValidity());
      form.querySelectorAll('.contact-form__inline-error').forEach((message) => message.remove());

      if (!invalid) return;

      event.preventDefault();
      const message = document.createElement('p');
      message.className = 'contact-form__inline-error';
      message.textContent = invalid.validationMessage;
      invalid.setAttribute('aria-invalid', 'true');
      invalid.insertAdjacentElement('afterend', message);
      invalid.focus();
    });
  });
}

function initPredictiveSearch() {
  document.querySelectorAll('[data-predictive-search-form]').forEach((form) => {
    const input = form.querySelector('[data-predictive-search-input]');
    const results = form.querySelector('[data-predictive-search-results]');
    if (!input || !results) return;

    let timer = null;
    let controller = null;

    const clear = () => {
      results.hidden = true;
      results.innerHTML = '';
    };

    input.addEventListener('input', () => {
      const query = input.value.trim();
      window.clearTimeout(timer);

      if (query.length < 2) {
        controller?.abort();
        clear();
        return;
      }

      timer = window.setTimeout(async () => {
        controller?.abort();
        controller = new AbortController();

        try {
          const url = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,page,article&resources[limit]=6`;
          const response = await fetch(url, {
            signal: controller.signal,
            headers: { Accept: 'application/json' }
          });

          if (!response.ok) return;

          const data = await response.json();
          const resources = data.resources?.results || {};
          const items = [
            ...(resources.products || []),
            ...(resources.pages || []),
            ...(resources.articles || [])
          ].slice(0, 6);

          if (!items.length) {
            results.innerHTML = `<p class="search-page__predictive-empty">${escapeHtml(window.themeStrings?.noSearchResults || 'No matching products found.')}</p>`;
            results.hidden = false;
            return;
          }

          results.innerHTML = items.map((item) => {
            const image = item.image ? `<img src="${item.image}" alt="${escapeHtml(item.title)}" width="48" height="48" loading="lazy">` : '<span aria-hidden="true"></span>';
            return `
              <a href="${item.url}">
                ${image}
                <strong>${escapeHtml(item.title)}</strong>
              </a>
            `;
          }).join('');
          results.hidden = false;
        } catch (error) {
          if (error.name !== 'AbortError') console.warn('Predictive search failed:', error);
        }
      }, 180);
    });

    document.addEventListener('click', (event) => {
      if (!form.contains(event.target)) clear();
    });
  });
}

function initLocalizationForms() {
  document.querySelectorAll('[data-localization-select]').forEach((select) => {
    select.addEventListener('change', () => select.form?.submit());
  });
}

function initContactContext(root = document) {
  const params = new URLSearchParams(window.location.search);
  const product = params.get('product');
  const intent = params.get('intent');
  if (!product && !intent) return;
  root.querySelectorAll('.contact-form__fields').forEach((form) => {
    const productInput = form.querySelector('[data-contact-product]');
    const intentInput = form.querySelector('[data-contact-intent]');
    const message = form.querySelector('[name="contact[body]"]');
    if (productInput) productInput.value = product || '';
    if (intentInput) intentInput.value = intent || '';
    if (message && !message.value.trim() && product) {
      const intentText = intent === 'bulk' ? 'bulk purchasing' : intent === 'personal' ? 'personal purchasing' : 'availability';
      message.value = `I'm interested in ${product.replace(/-/g, ' ')} and would like information about ${intentText}.`;
    }
  });
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-product-section]').forEach((section) => new ProductVariantController(section));
  document.querySelectorAll('[data-cart-drawer]').forEach((drawer) => new CartDrawer(drawer));
  initProductLightbox();
  hideMissingProductAnchors();
  initContactValidation();
  initPredictiveSearch();
  initQuickAddForms();
  initRandomizedProductGrids();
  initCollectionFilters();
  initLocalizationForms();
  initContactContext();
  document.querySelectorAll('[data-sort-by-select]').forEach((select) => {
    select.addEventListener('change', () => select.form?.submit());
  });

  const backToTop = document.querySelector('[data-back-to-top]');
  if (backToTop) {
    const toggleBackToTop = () => {
      backToTop.classList.toggle('is-visible', window.scrollY > 640);
    };

    backToTop.addEventListener('click', () => window.scrollTo({ top: 0, behavior: 'smooth' }));
    window.addEventListener('scroll', toggleBackToTop, { passive: true });
    toggleBackToTop();
  }
});

document.addEventListener('shopify:section:load', (event) => {
  initRandomizedProductGrids(event.target);
  initQuickAddForms();
});

/* ── AJAX Cart ── */

class AjaxCart extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('#CartForm');
    if (!this.form) return;

    this.debounceTimers = new Map();

    this.form.addEventListener('click', (e) => {
      const minus = e.target.closest('[data-qty-minus]');
      const plus = e.target.closest('[data-qty-plus]');
      const remove = e.target.closest('[data-remove-line]');

      if (minus) {
        const input = minus.parentElement.querySelector('[data-qty-input]');
        if (input && input.value > 0) {
          input.value = Number(input.value) - 1;
          this.scheduleUpdate(input);
        }
      }

      if (plus) {
        const input = plus.parentElement.querySelector('[data-qty-input]');
        if (input) {
          input.value = Number(input.value) + 1;
          this.scheduleUpdate(input);
        }
      }

      if (remove) {
        const key = remove.dataset.removeLine;
        if (key) this.updateItem(key, 0);
      }
    });

    this.form.querySelectorAll('[data-qty-input]').forEach((input) => {
      input.addEventListener('change', () => this.scheduleUpdate(input));
    });
  }

  scheduleUpdate(input) {
    const key = input.dataset.lineKey;
    clearTimeout(this.debounceTimers.get(key));
    this.debounceTimers.set(key, setTimeout(() => {
      this.updateItem(key, Number(input.value));
    }, 400));
  }

  async updateItem(key, quantity) {
    this.form.classList.add('cart-form--loading');
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });
      const cart = await res.json();

      const total = this.querySelector('#CartTotal');
      if (total) total.textContent = this.formatMoney(cart.total_price);
      updateCartCount(cart.item_count);

      const updatedItem = cart.items.find((item) => item.key === key);
      const line = this.querySelector(`[data-line-key="${key}"]`);
      const linePrice = line?.querySelector('[data-line-price]');
      if (linePrice && updatedItem) {
        linePrice.textContent = this.formatMoney(updatedItem.final_line_price);
      }

      if (quantity === 0) {
        if (line) {
          line.style.transition = 'opacity 220ms ease, transform 220ms ease';
          line.style.opacity = '0';
          line.style.transform = 'translateX(-20px)';
          setTimeout(() => line.remove(), 240);
        }
      }

      if (cart.item_count === 0) {
        setTimeout(() => location.reload(), 300);
      }
    } catch (err) {
      console.error('Cart update failed:', err);
    } finally {
      this.form.classList.remove('cart-form--loading');
    }
  }

  formatMoney(cents) {
    return formatThemeMoney(cents);
  }
}

if (!customElements.get('ajax-cart')) {
  customElements.define('ajax-cart', AjaxCart);
}

class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this.randomizeGrid();

    if (!this.dataset.url || !window.fetch) return;

    fetch(this.dataset.url)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;

        const fetchedGrid = html.querySelector('product-recommendations .product-grid');
        const currentGrid = this.querySelector('.product-grid');

        if (fetchedGrid && fetchedGrid.children.length > 0) {
          if (currentGrid) {
            currentGrid.replaceWith(fetchedGrid);
          } else {
            this.appendChild(fetchedGrid);
          }
          initQuickAddForms();
        }

        this.randomizeGrid();
      })
      .catch((error) => {
        console.error('Product recommendations failed:', error);
        this.randomizeGrid();
      });
  }

  randomizeGrid() {
    const grid = this.querySelector('[data-randomized-product-grid]');
    randomizeProductGrid(grid, this.dataset.cardCount);
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
